import { useReactFlow } from "@xyflow/react";
import { useCallback, useRef } from "react";
import {
  normalizeCanvasViewportTarget,
  type CanvasViewportTarget,
} from "./canvasViewportTarget";

/**
 * The one entry point for programmatic viewport moves on a canvas, editor
 * and public viewer alike: hand it a CanvasViewportTarget and the canvas
 * centers on it. Works anywhere inside the canvas's ReactFlowProvider
 * (nodes, the block lightbox, the page itself). Callers that arrive with a
 * target in some other form (the URL hash, a block link) adapt to it here;
 * the interrupt policy and the other movers will sit behind it too.
 *
 * Returns a stable function: `false`, without moving, for an unusable target
 * or before the pan-zoom exists; `true` once the move has been issued.
 */
export function useCanvasViewportNavigator(): (
  target: CanvasViewportTarget,
) => boolean {
  const reactFlow = useReactFlow();
  const reactFlowRef = useRef(reactFlow);
  reactFlowRef.current = reactFlow;
  return useCallback((target: CanvasViewportTarget) => {
    const normalized = normalizeCanvasViewportTarget(target);
    const flow = reactFlowRef.current;
    if (!normalized || !flow.viewportInitialized) return false;
    // setCenter defaults to maxZoom when no zoom is given; pass the current one.
    void flow.setCenter(normalized.x, normalized.y, {
      zoom: normalized.zoom ?? flow.getZoom(),
      duration: normalized.duration,
    });
    return true;
  }, []);
}
